import {
  ActionIcon,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconMail, IconSend, IconX } from '@tabler/icons-react';
import { getCoreRowModel, type RowSelectionState, useReactTable } from '@tanstack/react-table';
import { FilterButton } from 'components/FilterButton';
import { iconProps } from 'components/icon-props';
import { usePagination } from 'hooks/use-pagination';
import { i18nKeys, useTranslation } from 'locales';
import { countActiveFilters } from 'modules/Kanban/utils/filters';
import {
  MailFilterDrawer,
  MailRow,
  MailSelectAllBanner,
  MailToolbar,
} from 'modules/Mails/components';
import { useBuildMailColumns, useMailFilters } from 'modules/Mails/hooks';
import { useCanSendMail } from 'modules/Mails/hooks/use-can-send-mail';
import { useLoadMails } from 'modules/Mails/hooks/use-load-mails';
import { NewMail } from 'modules/Mails/pages/NewMail/NewMail';
import type { MailboxType } from 'modules/Mails/types/mailbox-type';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useUpdateThread } from 'shared/hooks/use-update-thread';
import { handleRowAuxClick, handleRowClick } from 'shared/utils/table';

interface MailIndexProps {
  mailboxType: MailboxType;
}

export const MailIndex = ({ mailboxType }: MailIndexProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { updateThread } = useUpdateThread({ disableSuccessNotification: true });

  const { form, filterParams, resetFilters } = useMailFilters();
  const [debouncedFilterParams] = useDebouncedValue(filterParams, 300);
  const [filterDrawerOpened, { open: openFilterDrawer, close: closeFilterDrawer }] =
    useDisclosure(false);
  const { canUseExternalMail, customDomain, isSameDomain } = useCanSendMail();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isSelectAllPages, setIsSelectAllPages] = useState(false);
  const { currentPage, pageLimit, PaginationFooter } = usePagination({
    indexPath: 'mails',
    overridePageLimit: 50,
  });

  const { mails, isMailsInitialLoading } = useLoadMails(mailboxType, {
    page: currentPage,
    pageLimit,
    ...debouncedFilterParams,
  });

  useEffect(() => {
    setIsSelectAllPages(false);
  }, [rowSelection]);

  const columns = useBuildMailColumns(true);
  const table = useReactTable({
    data: mails.threads,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
  });

  const handleNewMail = () => {
    modals.open({
      title: t(i18nKeys.EXTERNAL_MAIL.COMPOSE_TITLE),
      size: 'auto',
      centered: true,
      children: <NewMail />,
    });
  };

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
    setIsSelectAllPages(false);
  }, []);

  const sendMailButtonTitle = () => {
    if (customDomain && isSameDomain) return '';
    return t(customDomain ? i18nKeys.CUSTOM_DOMAIN.NO_MATCH : i18nKeys.CUSTOM_DOMAIN.NO_DOMAIN);
  };

  const isEmpty = !isMailsInitialLoading && (mails?.threads.length ?? 0) === 0;
  const totalItems = mails.pagination.totalObjects;
  const activeFiltersCount = countActiveFilters(filterParams, ['sortBy', 'sortOrder']);

  return (
    <Stack gap="space4" h="100%">
      <Group justify="space-between">
        <Tooltip disabled={canUseExternalMail} label={sendMailButtonTitle()}>
          <Button
            disabled={!canUseExternalMail}
            leftSection={<IconSend size={16} stroke={1.5} />}
            onClick={handleNewMail}
          >
            {t(i18nKeys.EXTERNAL_MAIL.NEW)}
          </Button>
        </Tooltip>
        <Group gap="sm">
          <TextInput
            w={250}
            placeholder={t(i18nKeys.FORM.QUICK_SEARCH.PLACEHOLDER)}
            rightSection={
              form.values.quickSearch ? (
                <ActionIcon
                  variant="light"
                  c="gray"
                  onClick={() => form.setFieldValue('quickSearch', '')}
                >
                  <IconX {...iconProps(16)} />
                </ActionIcon>
              ) : null
            }
            {...form.getInputProps('quickSearch')}
          />
          <FilterButton
            activeFiltersCount={activeFiltersCount}
            openFilters={openFilterDrawer}
            resetFilters={resetFilters}
          />
        </Group>
      </Group>
      <Card radius={8} p={0} mb="space6">
        <MailToolbar
          table={table}
          onClearSelection={handleClearSelection}
          isSelectAllPages={isSelectAllPages}
          form={form}
          showCategories={true}
          totalCount={totalItems}
        />
        {table.getIsAllRowsSelected() && totalItems > pageLimit && (
          <MailSelectAllBanner
            pageCount={table.getRowModel().rows.length}
            totalCount={totalItems}
            isSelectAllPages={isSelectAllPages}
            onToggle={() => setIsSelectAllPages(!isSelectAllPages)}
          />
        )}
        <Group style={{ overflowY: 'auto' }}>
          <Table
            stickyHeader
            stickyHeaderOffset={0}
            fz={12}
            highlightOnHover
            style={{ tableLayout: 'fixed', width: '100%' }}
          >
            <colgroup>
              {table.getAllColumns().map(col => (
                <col key={col.id} style={{ width: col.getSize() }} />
              ))}
            </colgroup>
            <Table.Tbody>
              {table.getRowModel().rows.map(row => (
                <MailRow
                  key={row.id}
                  row={row}
                  isSelected={row.getIsSelected()}
                  onClick={e => {
                    if ((e.target as HTMLElement).closest('button, a, input')) return;
                    if (!row.original.mailDetail.read)
                      updateThread({ id: row.original.id, read: true });
                    handleRowClick(e, `/mails/${row.original.id}`, history.push);
                  }}
                  onAuxClick={e => handleRowAuxClick(e, `/mails/${row.original.id}`)}
                />
              ))}
            </Table.Tbody>
          </Table>
          {isEmpty && (
            <Center mb="md" w="100%">
              <Stack align="center">
                <IconMail size={100} color="gray" stroke={1.5} />
                <Text>{t(i18nKeys.NO_RESULT)}</Text>
              </Stack>
            </Center>
          )}
          {isMailsInitialLoading && (
            <Center mb="md" w="100%">
              <Loader />
            </Center>
          )}
        </Group>
        <PaginationFooter total={totalItems} />
      </Card>
      <MailFilterDrawer isOpen={filterDrawerOpened} onClose={closeFilterDrawer} form={form} />
    </Stack>
  );
};
